import { forwardRef, type CSSProperties, type ReactNode } from "react";
import { motion } from "framer-motion";
import { EASE_BRAND, MOTION_DURATION } from "@/lib/motion";
import { useReducedMotionSafe } from "@/hooks/useReducedMotionSafe";
import ProtectedVideoPlayer from "@/components/shared/ProtectedVideoPlayer";
import { Link, useLocation } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import CtaTrustLine from "@/components/shared/CtaTrustLine";
import { trackEvent } from "@/lib/track-event";

interface PageHeroCta {
  label: string;
  to: string;
  secondaryLabel?: string;
  secondaryTo?: string;
  context?: string;
}

interface PageHeroProps {
  headline: string;
  subline: string;
  /** HSL triple like "12 88% 58%"; drives accent color via inline --accent var. */
  accentHsl?: string;
  /** Small label rendered above the headline (e.g. "Fotobox"). */
  eyebrow?: string;
  /** Optional icon shown left of the eyebrow / above the headline. */
  icon?: ReactNode;
  /** Optional product/visual stage image rendered on the right (desktop split). */
  imageSrc?: string;
  imageAlt?: string;
  /** Optional hero video; replaces the stage image when set. */
  videoSrc?: string;
  videoPoster?: string;
  videoLang?: "de" | "en";
  /** Videos brauchen vor dem Laden ein stabiles Format; Bilder nutzen ihr Originalformat. */
  mediaAspect?: "landscape" | "portrait";
  cta?: PageHeroCta;
}

const PageHero = forwardRef<HTMLElement, PageHeroProps>(
  ({ headline, subline, accentHsl, eyebrow, icon, imageSrc, imageAlt, videoSrc, videoPoster, videoLang, mediaAspect = "landscape", cta }, ref) => {
    const reduced = useReducedMotionSafe();
    const location = useLocation();
    const styleVars = accentHsl
      ? ({ ["--hero-accent" as any]: accentHsl } as CSSProperties)
      : undefined;

    const hasMedia = Boolean(imageSrc || videoSrc);

    return (
      <section
        ref={ref}
        style={styleVars}
        data-cta-primary={cta ? "hero_cta" : undefined}
        data-cta-context={cta?.context}
        data-cta-label={cta?.label}
        className="relative overflow-hidden section-light py-6 sm:py-8 lg:py-10"
      >
        <div className="container mx-auto px-4 lg:px-8 relative">
          <div className={`section-frame overflow-hidden rounded-2xl bg-card ${hasMedia ? "grid md:grid-cols-[1.15fr_0.85fr] items-center" : ""}`}>
            {/* REGEL: Seiten-Hero nutzt dieselbe Marken-Bewegung wie die Startseite
                (src/lib/motion.ts) und steht bei "Bewegung reduzieren" still. */}
            <motion.div
              initial={reduced ? false : { opacity: 0, y: 20, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: MOTION_DURATION.slow, ease: EASE_BRAND }}
              className={`${hasMedia ? "max-w-2xl" : "max-w-4xl"} px-6 py-12 sm:px-10 sm:py-16 lg:px-14 lg:py-20`}
            >
              {(eyebrow || icon) && (
                <div className="flex items-center gap-2.5 mb-4 sm:mb-5">
                  {icon && (
                    <span
                      className="inline-flex items-center justify-center w-9 h-9 rounded-md shrink-0"
                      style={
                        accentHsl
                          ? {
                              background: `hsl(var(--hero-accent) / 0.12)`,
                              color: `hsl(var(--hero-accent))`,
                            }
                          : { background: "hsl(var(--accent))", color: "hsl(var(--accent-foreground))" }
                      }
                      aria-hidden
                    >
                      {icon}
                    </span>
                  )}
                  {eyebrow && (
                    <span
                      className="text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase"
                      style={accentHsl ? { color: `hsl(var(--hero-accent))` } : undefined}
                    >
                      {eyebrow}
                    </span>
                  )}
                </div>
              )}
              <h1 className="text-3xl sm:text-5xl lg:text-6xl editorial-heading text-foreground mb-4 sm:mb-6">
                {headline}
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed">
                {subline}
              </p>
              {cta ? (
                <div className="mt-7">
                  <div className="flex w-full flex-col items-stretch gap-3 sm:flex-row sm:items-center">
                    <Button asChild className="h-auto w-full whitespace-normal bg-cta px-7 py-3 text-base font-semibold text-cta-foreground shadow-md hover:bg-cta-hover sm:w-auto">
                      <Link to={cta.to} onClick={() => trackEvent("cta_click", { button: "hero_cta", context: cta.context ?? "default", page: location.pathname })}>
                        {cta.label}<ArrowRight className="h-4 w-4" aria-hidden="true" />
                      </Link>
                    </Button>
                    {cta.secondaryLabel && cta.secondaryTo ? (
                      <Button asChild variant="link" className="h-auto px-2 text-foreground">
                        <Link to={cta.secondaryTo}>{cta.secondaryLabel}</Link>
                      </Button>
                    ) : null}
                  </div>
                  <CtaTrustLine prefix={videoLang === "en" ? "/en" : ""} className="justify-start" />
                </div>
              ) : null}
            </motion.div>

            {hasMedia && (
              <motion.div
                initial={reduced ? false : { opacity: 0, scale: 0.96, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: MOTION_DURATION.slow, ease: EASE_BRAND, delay: 0.1 }}
                className="relative flex w-full self-center justify-center p-4 sm:p-6"
              >
                {videoSrc ? (
                  <div
                    className={`relative w-full overflow-hidden rounded-lg section-night ${mediaAspect === "portrait" ? "aspect-[9/16] max-w-sm" : "aspect-video"}`}
                    style={accentHsl ? { boxShadow: `0 24px 60px -20px hsl(var(--hero-accent) / 0.35)` } : { boxShadow: "var(--shadow-medium)" }}
                  >
                    <ProtectedVideoPlayer
                      src={videoSrc}
                      poster={videoPoster}
                      label={imageAlt ?? eyebrow ?? "Video"}
                      lang={videoLang ?? "de"}
                      className="absolute inset-0 h-full w-full"
                      videoClassName="object-contain"
                    />
                  </div>
                ) : (
                  <img decoding="async" fetchPriority="high"
                    src={imageSrc}
                    alt={imageAlt ?? ""}
                    loading="eager"
                    className="block max-h-[42rem] w-auto max-w-full rounded-lg object-contain"
                    style={accentHsl ? { boxShadow: `0 24px 60px -20px hsl(var(--hero-accent) / 0.35)` } : { boxShadow: "var(--shadow-medium)" }}
                  />
                )}
              </motion.div>
            )}
          </div>
        </div>
      </section>
    );
  }
);

PageHero.displayName = "PageHero";

export default PageHero;
